import { BillPageConfig } from '../utils/billConfig';

// Google Sheets configuration
// NOTE: These API keys are intentionally client-side and safe to expose in build output.
// Google Sheets API keys are designed for public use with proper domain/referrer restrictions.
const SPREADSHEET_ID = process.env.REACT_APP_GOOGLE_SHEETS_ID || '';
const SHEET_NAME = 'Bill Pages';
const API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY || '';

export interface SheetsError {
  message: string;
  code?: string;
}

export interface BillData {
  country: string;
  state: string;
  billTitle: string;
  billUrl: string;
  billStatus: string;
  googleDriveUrl: string;
  notes: string;
  billId?: string;
}

interface GoogleSheetsResponse {
  values?: string[][];
  error?: {
    message: string;
    code: number;
  };
}

/**
 * Fetches bill pages data from Google Sheets using the REST API
 * Expected spreadsheet format:
 * Column A: route (e.g., "time-taken-time-back")
 * Column B: pageTitle (e.g., "Time Taken Time Back")
 * Column C: secondaryTitle (e.g., "Detour False Allegations")
 * Column D: shortDescription (e.g., "This law protects parents...")
 * Column E: seoTitle (e.g., "Time Taken, Time Back Law - Robert Garza")
 * Column F: seoDescription (e.g., "The Time Taken, Time Back Law restores...")
 * Column G: billId (optional, e.g., "TTTB001")
 */
export async function fetchBillPagesFromSheets(): Promise<BillPageConfig[]> {
  try {
    // Validate required environment variables
    if (!SPREADSHEET_ID) {
      throw new Error('REACT_APP_GOOGLE_SHEETS_ID environment variable is required');
    }
    if (!API_KEY) {
      throw new Error('REACT_APP_GOOGLE_SHEETS_API_KEY environment variable is required');
    }

    if (SPREADSHEET_ID === 'your_spreadsheet_id_here') {
      throw new Error(
        'SPREADSHEET_ID is still the placeholder value. Please update your .env file with: 1dyqTNCeep1d5CAECdXwo51QRL3X_ksoM7FW2uxyEPvw',
      );
    }

    // Define the range to fetch (starting from row 2 to skip headers)
    // Note: Adjusting to fetch columns B-I since column A appears to contain row numbers
    const range = `${SHEET_NAME}!B2:I`;

    // Build the API URL
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;

    // Fetch data from the Google Sheets API
    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Google Sheets API error (${response.status}): ${errorText}`);
    }

    const data: GoogleSheetsResponse = await response.json();

    if (data.error) {
      throw new Error(`Google Sheets API error: ${data.error.message}`);
    }

    const rows = data.values;

    if (!rows || rows.length === 0) {
      return [];
    }

    // No need to skip header row since we're already fetching from B2:I (starting from row 2)
    const dataRows = rows;

    // Transform spreadsheet data to BillPageConfig objects
    const billPages: BillPageConfig[] = dataRows
      .filter((row) => row && row[0] && row[1]) // Ensure route (column B) and pageTitle (column C) exist
      .map((row, index) => {
        try {
          const billPage: BillPageConfig = {
            route: String(row[0] || '').trim(), // Column B: Page Link (route)
            pageTitle: String(row[1] || '').trim(), // Column C: Page Title
            secondaryTitle: String(row[2] || '').trim(), // Column D: Secondary Title
            shortDescription: String(row[3] || '').trim(), // Column E: Short Description
            seoTitle: String(row[4] || row[1] || '').trim(), // Column F: SEO Title
            seoDescription: String(row[5] || row[3] || '').trim(), // Column G: SEO Description
            billId: undefined, // No billId in current sheet structure
          };

          // Validate required fields
          if (!billPage.route || !billPage.pageTitle) {
            return null;
          }

          return billPage;
        } catch (error) {
          return null;
        }
      })
      .filter((billPage): billPage is BillPageConfig => billPage !== null);

    return billPages;
  } catch (error) {
    // Re-throw with more context
    if (error instanceof Error) {
      throw new Error(`Google Sheets API error: ${error.message}`);
    }

    throw new Error('Unknown error occurred while fetching from Google Sheets');
  }
}

/**
 * Convert any Google Drive/Docs edit URL to a public-viewable URL.
 * Uploaded .docx files are not native Google Docs, so they need
 * drive.google.com/file/d/ID/view rather than docs.google.com/.../preview.
 */
function toPublicDriveUrl(url: string): string {
  // Extract file ID from docs.google.com/document/d/ID/... or drive.google.com/file/d/ID/...
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match) {
    return `https://drive.google.com/file/d/${match[1]}/view`;
  }
  return url;
}

/**
 * Fetch bill data from Google Sheets for all states
 */
export async function fetchBillDataFromSheets(): Promise<BillData[]> {
  try {
    // Validate required environment variables
    if (!SPREADSHEET_ID) {
      throw new Error('REACT_APP_GOOGLE_SHEETS_ID environment variable is required');
    }
    if (!API_KEY) {
      throw new Error('REACT_APP_GOOGLE_SHEETS_API_KEY environment variable is required');
    }

    // Fetch from Bill Data sheet — include header row (A1) for column auto-detection
    const BILL_DATA_SHEET = 'Bill Data';
    const range = `${BILL_DATA_SHEET}!A1:G`;

    // Build the API URL
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/${encodeURIComponent(range)}?key=${API_KEY}`;

    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error(`Invalid request to Google Sheets API. Check your sheet name and range: ${range}`);
      } else if (response.status === 403) {
        throw new Error('Google Sheets API access forbidden. Check your API key permissions.');
      } else if (response.status === 404) {
        throw new Error(`Spreadsheet or sheet not found. Check your spreadsheet ID and sheet name: ${BILL_DATA_SHEET}`);
      }

      throw new Error(`Google Sheets API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.values || data.values.length === 0) {
      return [];
    }

    // Auto-detect whether Country column exists by inspecting header row
    const headers = data.values[0].map((h: string) => String(h).trim().toLowerCase());
    const hasCountryColumn = headers[0] === 'country';
    const rows = data.values.slice(1); // skip header row

    const billData: BillData[] = rows
      .map((row: string[]) => {
        if (hasCountryColumn) {
          // New format: Country(A), State(B), BillTitle(C), BillStatus(D), BillId(E), BillUrl(F)
          // Column F holds either a Google Drive URL (legislator pack) or a state legislature URL
          const rawUrl = (row[5] || '').trim();
          const isGoogleUrl = rawUrl.toLowerCase().includes('google');
          // Convert docs.google.com/document edit URLs to drive.google.com/file/view
          // .docx files uploaded to Drive are not native Google Docs and need the /file/view path
          const googleDriveUrl = isGoogleUrl
            ? toPublicDriveUrl(rawUrl)
            : '';
          return {
            country: (row[0] || '').trim(),
            state: (row[1] || '').trim(),
            billTitle: (row[2] || '').trim(),
            billStatus: (row[3] || '').trim(),
            billId: (row[4] || '').trim(),
            billUrl: isGoogleUrl ? '' : rawUrl,
            googleDriveUrl,
            notes: (row[6] || '').trim(),
          };
        }
        // Legacy format (no Country column): State(A), BillTitle(B), BillStatus(C), BillId(D), BillUrl(E)
        const rawLegacyUrl = (row[4] || '').trim();
        const isLegacyGoogleUrl = rawLegacyUrl.toLowerCase().includes('google');
        const legacyGoogleDriveUrl = isLegacyGoogleUrl
          ? toPublicDriveUrl(rawLegacyUrl)
          : '';
        return {
          country: 'United States',
          state: row[0] || '',
          billTitle: row[1] || '',
          billStatus: row[2] || '',
          billId: row[3] || '',
          billUrl: isLegacyGoogleUrl ? '' : rawLegacyUrl,
          googleDriveUrl: legacyGoogleDriveUrl,
          notes: row[5] || '',
        };
      })
      .filter(
        (bill: BillData) =>
          bill.country.trim() !== '' && bill.state.trim() !== '' && bill.billTitle.trim() !== '',
      );

    return billData;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Google Sheets Bill Data API error: ${error.message}`);
    }

    throw new Error('Unknown error occurred while fetching bill data from Google Sheets');
  }
}

/**
 * Get bill data for a specific state, optionally filtered by country
 */
export async function getBillDataForState(state: string, country?: string): Promise<BillData[]> {
  try {
    const allBillData = await fetchBillDataFromSheets();
    let filtered = allBillData;
    if (country) {
      filtered = filtered.filter((bill) => bill.country.toLowerCase() === country.toLowerCase());
    }
    return filtered.filter((bill) => bill.state.toLowerCase() === state.toLowerCase());
  } catch {
    return [];
  }
}

/**
 * Get CTA (Call to Action) information for a bill URL
 */
export function getBillCTAInfo(billUrl: string): {
  callText: string;
  actionText: string;
  action: 'scroll' | 'external';
  ctaText: string;
} {
  // Simple CTA logic based on bill URL patterns
  if (billUrl.includes('legislature.wa.gov')) {
    return {
      callText: 'Contact Washington State Legislature',
      actionText: 'Support this bill',
      action: 'external',
      ctaText: 'View Bill',
    };
  }

  if (billUrl.includes('legis.state')) {
    return {
      callText: 'Contact State Legislature',
      actionText: 'Support this legislation',
      action: 'external',
      ctaText: 'View Bill',
    };
  }

  return {
    callText: 'Contact your representatives',
    actionText: 'Support this bill',
    action: 'external',
    ctaText: 'View Bill',
  };
}
